import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  ActivityIndicator,
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import DeviceInfo from 'react-native-device-info';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useTheme, GTFonts } from '../../../theme';
import { fs, s, v, iconSize, borderWidth } from '../../../utils/libs';
import DemoProductCard from './DemoProductCard';
import {
  useDemoCart,
  useDemoProducts,
  type DemoProductItem,
} from '../../../hooks/demo';
import { useDetectedCountry } from '../../../hooks/useDetectedCountry';
import { ExtrasContext } from '../../../Context/ExtrasContext';
import CustomBottomSheet from '../../../components/Reusables/bottomSheet/CustomBottomSheet';
import ProductDetailBottomSheet, {
  type ProductDetailCartBridge,
} from '../../../components/ProductDetailBottomSheet';
import Typography from '../../../components/Reusables/Typography';
import {
  demoProductInStock,
  toDemoSheetProduct,
} from '../../../utils/demoProductAdapter';
import {
  getPriceRangeDisplay,
  isVariantProduct,
} from '../../../utils/variantHelpers';
import type { ProductObject } from '../../../redux/productSlice';

const tablet = DeviceInfo.isTablet();

interface DemoProductListNativeProps {
  onCartCountChange?: (count: number) => void;
  searchQuery?: string;
}

const DemoProductListNative: React.FC<DemoProductListNativeProps> = ({
  onCartCountChange,
  searchQuery = '',
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [subcategory, setSubcategory] = useState('AllProducts');
  const [subcategoryPickerOpen, setSubcategoryPickerOpen] = useState(false);
  const subcategoryHeaderListRef = useRef<FlatList<string>>(null);
  const subcategoryPickerSheetRef = useRef<BottomSheetModal>(null as any);
  const productDetailBottomSheetRef = useRef<BottomSheetModal>(null as any);
  const [selectedSheetProduct, setSelectedSheetProduct] = useState<any | null>(
    null,
  );
  const { isRowLayout } = useContext(ExtrasContext);
  const { currency } = useDetectedCountry();

  const { products, isLoading, error, getCategories } = useDemoProducts();
  const {
    cartItems,
    cartCount,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    updateCartItemMass,
    setDemoCartItemQuantity,
    setDemoCartUnitMass,
    refetch: refetchDemoCart,
  } = useDemoCart();

  const productsById = useMemo(() => {
    const map = new Map<string, DemoProductItem>();
    products.forEach(p => map.set(p._id, p));
    return map;
  }, [products]);

  useEffect(() => {
    onCartCountChange?.(cartCount);
  }, [cartCount, onCartCountChange]);

  useFocusEffect(
    useCallback(() => {
      void refetchDemoCart();
    }, [refetchDemoCart]),
  );

  const allSubCategories = useMemo(() => {
    const categories = getCategories();
    return ['AllProducts', ...categories];
  }, [getCategories]);

  const filteredProducts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    let baseData =
      subcategory === 'AllProducts'
        ? products
        : products.filter(item => item.bp_product_category === subcategory);

    if (query.length > 1) {
      baseData = baseData.filter(
        item =>
          item.bp_product_name.toLowerCase().includes(query) ||
          item.bp_product_barcode.includes(searchQuery) ||
          item.bp_product_category.toLowerCase().includes(query),
      );
    }

    return baseData;
  }, [products, subcategory, searchQuery]);

  const highlightedProductId = useMemo(() => {
    const first = filteredProducts.find(
      p =>
        p.bp_product_price_type !== 'unit' &&
        !isVariantProduct(p as unknown as ProductObject) &&
        demoProductInStock(p),
    );
    return first?._id ?? null;
  }, [filteredProducts]);

  const fetchProductsBySubCategory = useCallback((value: string) => {
    setSubcategory(value);
  }, []);

  useEffect(() => {
    if (subcategoryPickerOpen) {
      subcategoryPickerSheetRef.current?.present();
    } else {
      subcategoryPickerSheetRef.current?.dismiss();
    }
  }, [subcategoryPickerOpen]);

  const getCartMetaForProduct = useCallback(
    (productId: string, variantId?: string | null) => {
      const cartItem = cartItems.find(
        item =>
          item.product_id === productId &&
          (item.variant_id ?? '') === (variantId ?? ''),
      );
      return {
        quantityInCart: cartItem?.quantity ?? 0,
        massInCart: cartItem?.mass ?? 0,
        cartItemId: cartItem?._id,
      };
    },
    [cartItems],
  );

  const handleProductPress = useCallback((product: DemoProductItem) => {
    setSelectedSheetProduct(toDemoSheetProduct(product));
    requestAnimationFrame(() => {
      productDetailBottomSheetRef.current?.present();
    });
  }, []);

  const handleSubproductQuantity = useCallback(
    (sheetProduct: any) => {
      const product = productsById.get(sheetProduct?.productID);
      if (!product) return;
      const variantId = sheetProduct?.variantId;
      const { cartItemId, quantityInCart } = getCartMetaForProduct(
        product._id,
        variantId,
      );
      if (!cartItemId) return;
      void updateCartItemQuantity(
        cartItemId,
        Math.max(0, quantityInCart - 1),
      );
    },
    [getCartMetaForProduct, productsById, updateCartItemQuantity],
  );

  const cartBridge = useMemo<ProductDetailCartBridge>(
    () => ({
      cartItems: cartItems.map(item => ({
        productID: item.product_id,
        variantId: item.variant_id,
        Quantity: item.quantity,
        mass: item.mass,
      })),
      onAdd: (payload: any) => {
        const product = productsById.get(payload?.productID);
        if (!product) return;
        const variantId = payload?.variantId;
        if (payload?.price_type === 'unit') {
          setDemoCartUnitMass(product, Number(payload?.mass ?? 0), {
            variantId,
          });
          return;
        }
        void addToCart(product, 1, 0, { variantId });
      },
      onSetQuantity: ({ productID, variantId, quantity }) => {
        const product = productsById.get(productID);
        if (!product) return;
        setDemoCartItemQuantity(product, quantity, { variantId });
      },
      onRemove: ({ productID, variantId }) => {
        const { cartItemId } = getCartMetaForProduct(productID, variantId);
        if (cartItemId) {
          void removeFromCart(cartItemId);
        }
      },
    }),
    [
      addToCart,
      cartItems,
      getCartMetaForProduct,
      productsById,
      removeFromCart,
      setDemoCartItemQuantity,
      setDemoCartUnitMass,
    ],
  );

  const handlePickSubcategory = useCallback(
    (name: string) => {
      setSubcategoryPickerOpen(false);
      const index = allSubCategories.findIndex(item => item === name);
      if (index >= 0) {
        try {
          subcategoryHeaderListRef.current?.scrollToIndex({
            index,
            animated: true,
            viewPosition: 0.5,
          });
        } catch {}
      }
      fetchProductsBySubCategory(name);
    },
    [allSubCategories, fetchProductsBySubCategory],
  );

  const isUnitType = useCallback(
    (product: DemoProductItem) => product.bp_product_price_type === 'unit',
    [],
  );

  const handleAdd = useCallback(
    async (product: DemoProductItem) => {
      if (isVariantProduct(product as unknown as ProductObject)) {
        handleProductPress(product);
        return;
      }
      const { cartItemId, quantityInCart, massInCart } = getCartMetaForProduct(
        product._id,
      );

      if (!cartItemId) {
        await addToCart(
          product,
          isUnitType(product) ? 0 : 1,
          isUnitType(product) ? 1 : 0,
        );
        return;
      }

      if (isUnitType(product)) {
        await updateCartItemMass(cartItemId, massInCart + 1);
      } else {
        await updateCartItemQuantity(cartItemId, quantityInCart + 1);
      }
    },
    [
      addToCart,
      getCartMetaForProduct,
      handleProductPress,
      isUnitType,
      updateCartItemMass,
      updateCartItemQuantity,
    ],
  );

  const handleRemove = useCallback(
    async (product: DemoProductItem) => {
      const { cartItemId, quantityInCart, massInCart } = getCartMetaForProduct(
        product._id,
      );
      if (!cartItemId) return;

      if (isUnitType(product)) {
        await updateCartItemMass(cartItemId, Math.max(0, massInCart - 1));
      } else {
        await updateCartItemQuantity(
          cartItemId,
          Math.max(0, quantityInCart - 1),
        );
      }
    },
    [
      getCartMetaForProduct,
      isUnitType,
      updateCartItemMass,
      updateCartItemQuantity,
    ],
  );

  const handleSetQuantity = useCallback(
    async (product: DemoProductItem, quantity: number) => {
      const { cartItemId } = getCartMetaForProduct(product._id);
      const safeQuantity = Math.max(0, Math.floor(quantity));

      if (!cartItemId) {
        if (safeQuantity > 0) {
          await addToCart(product, safeQuantity, 0);
        }
        return;
      }
      await updateCartItemQuantity(cartItemId, safeQuantity);
    },
    [addToCart, getCartMetaForProduct, updateCartItemQuantity],
  );

  const handleSetMass = useCallback(
    async (product: DemoProductItem, mass: number) => {
      const { cartItemId } = getCartMetaForProduct(product._id);
      const safeMass = Number.isFinite(mass) ? Math.max(0, mass) : 0;

      if (!cartItemId) {
        if (safeMass > 0) {
          await addToCart(product, 0, safeMass);
        }
        return;
      }
      await updateCartItemMass(cartItemId, safeMass);
    },
    [addToCart, getCartMetaForProduct, updateCartItemMass],
  );

  const numColumns = useMemo(
    () => (tablet ? (isRowLayout ? 2 : 4) : isRowLayout ? 1 : 2),
    [isRowLayout],
  );

  const renderSubCategory = useCallback(
    ({ item }: { item: string }) => {
      const isActive = subcategory === item;
      return (
        <View style={styles.Subcategorycontainer}>
          <Pressable
            onPress={() => fetchProductsBySubCategory(item)}
            hitSlop={6}>
            <View
              style={isActive ? styles.subCategoryPressed : styles.subCategory}>
              <Typography style={isActive ? styles.textPressed : styles.text}>
                {item}
              </Typography>
            </View>
          </Pressable>
        </View>
      );
    },
    [fetchProductsBySubCategory, styles, subcategory],
  );

  const renderPickerItem = useCallback(
    ({ item }: { item: string }) => {
      const isActive = item === subcategory;
      return (
        <Pressable
          onPress={() => handlePickSubcategory(item)}
          style={[
            styles.pickerRadioItem,
            isActive && styles.pickerRadioItemActive,
          ]}>
          <MaterialCommunityIcons
            name={isActive ? 'radiobox-marked' : 'radiobox-blank'}
            size={iconSize(20)}
            color={isActive ? theme.colors.success : theme.colors.text}
          />
          <Typography
            style={[
              styles.pickerRadioLabel,
              isActive && styles.pickerRadioLabelActive,
            ]}>
            {item}
          </Typography>
        </Pressable>
      );
    },
    [
      handlePickSubcategory,
      styles,
      subcategory,
      theme.colors.success,
      theme.colors.text,
    ],
  );

  const renderProduct = useCallback(
    ({ item }: { item: DemoProductItem }) => {
      const { quantityInCart, massInCart } = getCartMetaForProduct(item._id);
      const asProduct = item as unknown as ProductObject;
      const hasVariants = isVariantProduct(asProduct);
      return (
        <View style={[numColumns > 1 && { flex: 1, flexBasis: 0 }]}>
          <DemoProductCard
            product={item}
            quantityInCart={quantityInCart}
            massInCart={massInCart}
            isRowLayout={isRowLayout}
            currency={currency}
            highlightAddButton={highlightedProductId === item._id}
            hasVariants={hasVariants}
            variantCount={item.bp_product_variants?.length ?? 0}
            priceRangeText={
              hasVariants ? getPriceRangeDisplay(asProduct) : undefined
            }
            onProductPress={handleProductPress}
            onAdd={handleAdd}
            onRemove={handleRemove}
            onSetQuantity={handleSetQuantity}
            onSetMass={handleSetMass}
          />
        </View>
      );
    },
    [
      getCartMetaForProduct,
      numColumns,
      isRowLayout,
      currency,
      handleAdd,
      handleRemove,
      handleSetQuantity,
      handleSetMass,
      handleProductPress,
      highlightedProductId,
    ],
  );

  return (
    <View style={styles.productlist}>
      <View style={styles.subCategoryHeader}>
        {allSubCategories.length > 1 ? (
          <TouchableOpacity onPress={() => setSubcategoryPickerOpen(true)}>
            <View style={styles.subCategoryIconTouchable}>
              <View style={styles.subCategoryIconWrapper}>
                <MaterialCommunityIcons
                  name="format-list-bulleted"
                  size={iconSize(18)}
                  color={theme.colors.text}
                />
              </View>
            </View>
          </TouchableOpacity>
        ) : null}
        <FlatList
          ref={subcategoryHeaderListRef}
          data={allSubCategories}
          renderItem={renderSubCategory}
          horizontal
          keyExtractor={(item, idx) => `${item}-${idx}`}
          contentContainerStyle={styles.subCategoryListContent}
          showsHorizontalScrollIndicator
          onScrollToIndexFailed={info => {
            subcategoryHeaderListRef.current?.scrollToOffset({
              offset: Math.max(0, info.averageItemLength * info.index),
              animated: false,
            });
            setTimeout(() => {
              subcategoryHeaderListRef.current?.scrollToIndex({
                index: Math.min(info.index, allSubCategories.length - 1),
                animated: true,
                viewPosition: 0.5,
              });
            }, 0);
          }}
        />
      </View>

      <View style={styles.productsArea}>
        {isLoading ? (
          <View style={styles.centerState}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
          </View>
        ) : error ? (
          <View style={styles.centerState}>
            <Typography style={styles.errorText}>{error}</Typography>
          </View>
        ) : (
          <FlatList
            data={filteredProducts}
            renderItem={renderProduct}
            keyExtractor={item => item._id}
            numColumns={numColumns}
            key={numColumns}
            style={{ width: '100%' }}
            contentContainerStyle={styles.flatListContent}
            showsVerticalScrollIndicator
            ListEmptyComponent={
              <View style={styles.centerState}>
                <Typography style={styles.emptyText}>
                  No products found
                </Typography>
              </View>
            }
          />
        )}
      </View>
      <CustomBottomSheet
        bottomSheetModalRef={subcategoryPickerSheetRef}
        snapPoints={['85%']}
        enableDynamicSizing={false}
        handleSheetChanges={index => {
          if (index === -1) {
            setSubcategoryPickerOpen(false);
          }
        }}
        header={
          <Typography style={styles.bottomSheetTitle}>
            Select Category
          </Typography>
        }>
        <View style={styles.bottomSheet}>
          <FlatList
            data={allSubCategories}
            keyExtractor={(item, idx) => `${item}-${idx}`}
            renderItem={renderPickerItem}
            numColumns={2}
            columnWrapperStyle={styles.pickerRow}
            contentContainerStyle={styles.pickerListContent}
            showsHorizontalScrollIndicator={false}
          />
        </View>
      </CustomBottomSheet>
      <ProductDetailBottomSheet
        bottomSheetRef={productDetailBottomSheetRef}
        product={selectedSheetProduct}
        handleSubproductQuantity={handleSubproductQuantity}
        cartBridge={cartBridge}
      />
    </View>
  );
};

export default DemoProductListNative;

const createStyles = (theme: any) =>
  StyleSheet.create({
    productlist: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    subCategoryHeader: {
      alignItems: 'center',
      backgroundColor: theme.colors.primaryDark,
      justifyContent: 'center',
      flexDirection: 'row',
    },
    subCategoryListContent: {
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 0,
      backgroundColor: theme.colors.primaryDark,
    },
    subCategoryIconTouchable: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.primaryDark,
      paddingLeft: s(5),
      paddingRight: s(3),
      paddingVertical: s(2),
      marginBottom: v(1),
    },
    Subcategorycontainer: {
      flexDirection: 'row',
      paddingTop: v(10),
      marginBottom: v(7),
      backgroundColor: theme.colors.primaryDark,
    },
    subCategory: {
      borderRadius: s(5),
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: s(tablet ? 3 : 6),
      marginHorizontal: s(3),
      backgroundColor: theme.colors.surface,
      borderColor: theme.colors.primaryDark,
      paddingTop: v(5),
      marginTop: v(-5),
    },
    subCategoryPressed: {
      borderRadius: s(5),
      marginTop: -v(5),
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: s(5),
      marginHorizontal: s(2),
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    subCategoryIconWrapper: {
      marginTop: v(-1),
      paddingHorizontal: s(4),
      backgroundColor: theme.colors.surface,
      borderRadius: s(5),
      padding: s(4),
    },
    text: {
      fontFamily: GTFonts.regular,
      paddingHorizontal: s(8),
      color: theme.colors.text,
      lineHeight: fs(16),
      fontSize: fs(13),
    },
    textPressed: {
      fontFamily: GTFonts.semibold,
      paddingHorizontal: s(8),
      color: theme.colors.textInverse,
      lineHeight: fs(16),
      fontSize: fs(14),
    },
    productsArea: {
      flex: 1,
      position: 'relative',
    },
    flatListContent: {
      flexGrow: 1,
      paddingBottom: s(20),
      paddingTop: v(2),
      backgroundColor: theme.colors.background,
    },
    centerState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorText: {
      fontFamily: GTFonts.regular,
      color: theme.colors.error,
      fontSize: fs(14),
    },
    emptyText: {
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      fontSize: fs(14),
    },
    bottomSheet: {
      flex: 1,
      marginTop: v(15),
      alignItems: 'center',
      backgroundColor: theme.colors.surface,
    },
    bottomSheetTitle: {
      fontSize: fs(24),
      lineHeight: fs(28),
      textAlign: 'center',
      fontFamily: GTFonts.bold,
      marginBottom: v(10),
      color: theme.colors.text,
    },
    pickerListContent: {
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      paddingHorizontal: s(8),
      paddingBottom: s(8),
    },
    pickerRow: {
      width: '100%',
      justifyContent: 'space-between',
      gap: s(8),
      marginBottom: v(8),
    },
    pickerRadioItem: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      borderRadius: s(8),
      paddingVertical: s(12),
      paddingHorizontal: s(10),
      borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderColor: 'transparent',
      flex: 1,
      minHeight: v(tablet ? 56 : 64),
    },
    pickerRadioItemActive: {
      borderColor: theme.colors.success,
    },
    pickerRadioLabel: {
      marginLeft: s(6),
      color: theme.colors.text,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    pickerRadioLabelActive: {
      color: theme.colors.success,
    },
  });
